import React from "react";
import PropTypes from "prop-types";
import { formatRelativeVerbose } from "views_shared/app/javascript/utils/dateFormatters";
import { expertUrl } from "views_shared/app/javascript/utils/urls";
import MessageButton from "../../MessageButton";
import { initials } from "../../source_directory/utils";
import HighlightAttribute, { highlightValue } from "../../source_directory/HighlightAttribute";
import ExpertByline from "./ExpertByline";
import isOwnExpert from "../../experts/card/viewerGating";
import BodyTextWithExpand from "../../feeds/BodyTextWithExpand";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import useCallForSources from "./useCallForSources";

const SECONDARY_BTN_STYLE = {
  background: "#fff",
  border: "1px solid #dde1e5",
  color: "#21272a",
  borderRadius: 8,
  height: 40,
  boxShadow: "0px 1px 2px rgba(16,24,40,0.05)",
  fontWeight: 500,
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
};

function StoryIdeaCardGrid({ hit }) {
  const expert = hit.expert || {};
  const callForSources = useCallForSources(hit);
  const canMessage = !!globalThis.Qwoted?.CURRENT_USER_ID && !isOwnExpert(expert);
  const postedAt = hit.posted_at_int ? formatRelativeVerbose(new Date(hit.posted_at_int * 1000)) : null;
  const byline = [expert.job_title, expert.org_name].filter(Boolean).join(" at ");

  return (
    <div
      className="animated faster fadeIn"
      style={{
        gridRow: "span 3",
        display: "grid",
        gridTemplateRows: "subgrid",
        // Without this the column takes its min-content minimum and overflows the card, which clips it.
        gridTemplateColumns: "minmax(0, 1fr)",
        borderRadius: 14,
        border: "1px solid #d2d3d4",
        boxShadow: "0px 1px 1px rgba(15,20,36,0.04)",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Row 1: Header — expert */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "20px 20px 0" }}>
        {expert.avatar_url ? (
          <img
            src={expert.avatar_url}
            alt={expert.name}
            style={{ width: 56, height: 56, borderRadius: 28, objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              background: "#f5f9ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontWeight: 700,
              fontSize: 16,
              color: "#533dd1",
            }}
          >
            {initials(expert.name)}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
          <div
            className="sd-grid-card__name"
            style={{
              fontWeight: 700,
              fontSize: 17,
              lineHeight: "22.1px",
              color: "#212528",
              letterSpacing: "-0.085px",
            }}
          >
            {expert.slug ? (
              <a href={expertUrl({ slug: expert.slug, migrate_id: expert.migrate_id })} style={{ color: "#212528" }}>
                <HighlightAttribute hit={hit} attribute={["expert", "name"]} fallback={expert.name} />
              </a>
            ) : (
              <HighlightAttribute hit={hit} attribute={["expert", "name"]} fallback={expert.name} />
            )}
          </div>
          {byline && (
            <div
              style={{
                fontSize: 14,
                lineHeight: "20.3px",
                color: "#4d5357",
                marginTop: 4,
                overflowWrap: "break-word",
              }}
            >
              <ExpertByline expert={expert} orgLinkStyle={{ color: "#4d5357", textDecoration: "underline" }} />
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0 }}>
          <SavedGroupsBookmark
            sourceableType="Enrich::PublicPitch"
            sourceableId={hit.id}
            sourceMeta={{ name: hit.title }}
          />
        </div>
      </div>

      {/* Row 2: Content — headline + dek + posted time */}
      <div style={{ padding: "0 20px", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div
            className="sd-grid-card__headline"
            style={{
              fontWeight: 700,
              fontSize: 17,
              lineHeight: "22.95px",
              color: "#212528",
              letterSpacing: "-0.085px",
              overflowWrap: "break-word",
            }}
          >
            <HighlightAttribute hit={hit} attribute="title" fallback={hit.title} />
          </div>
          {hit.text && (
            <BodyTextWithExpand body={hit.text} maxLength={372} highlightValue={highlightValue(hit, "text")} />
          )}
        </div>
        {postedAt && (
          <div style={{ fontSize: 12, lineHeight: "12px", color: "#4d5357", marginTop: 12 }}>{postedAt}</div>
        )}
      </div>

      {/* Row 3: CTAs — always rendered to maintain 3-child count for subgrid */}
      {canMessage ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 20px 20px" }}>
          <a
            href={callForSources.href}
            onClick={callForSources.onClick}
            aria-busy={callForSources.isPreparing}
            className="btn text-nowrap d-flex align-items-center justify-content-center w-100 mb-0"
            style={{
              background: "#533dd1",
              border: "1px solid #533dd1",
              color: "#fff",
              borderRadius: 8,
              height: 40,
              gap: 6,
              fontWeight: 500,
              fontSize: 14,
              boxShadow: "0px 1px 1px rgba(16,24,40,0.04)",
            }}
          >
            <i className={callForSources.isPreparing ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-megaphone"} />
            Call for Experts/Products
          </a>
          <MessageButton
            promoEntityId={expert.promo_entity_id}
            promoEntityType={expert.promo_entity_type}
            buttonLabel="Message Expert"
            buttonClassName="btn"
            iconClassName="fa-regular fa-message"
            buttonStyle={SECONDARY_BTN_STYLE}
            extraButtonClasses="w-100 mb-0"
            openInboxThread
          />
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

StoryIdeaCardGrid.propTypes = {
  hit: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    posted_at_int: PropTypes.number,
    expert: PropTypes.shape({
      promo_entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      promo_entity_type: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
      avatar_url: PropTypes.string,
      job_title: PropTypes.string,
      org_name: PropTypes.string,
      org_slug: PropTypes.string,
    }),
  }).isRequired,
};

export default StoryIdeaCardGrid;
